import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import type { PasoTour } from "./tiposTour";

export interface CajaEnfoque {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** Une el rectángulo base con el de un elemento expandido (submenús, paneles). */
function unirConExpandido(base: DOMRect, selectorExpandido?: string): DOMRect {
  if (!selectorExpandido) return base;
  const extra = document.querySelector<HTMLElement>(selectorExpandido);
  if (!extra) return base;
  const r = extra.getBoundingClientRect();
  const top = Math.min(base.top, r.top);
  const left = Math.min(base.left, r.left);
  const right = Math.max(base.right, r.right);
  const bottom = Math.max(base.bottom, r.bottom);
  return new DOMRect(left, top, right - left, bottom - top);
}

const reduceMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/**
 * Calcula la caja de la sección enfocada y mantiene el estado "fuera",
 * que se activa cuando el usuario trabaja fuera del área resaltada.
 */
export function useEnfoqueTour(
  paso: PasoTour | null,
  elementoObjetivo: HTMLElement | null,
  refTarjeta: RefObject<HTMLElement | null>
) {
  const [caja, setCaja] = useState<CajaEnfoque | null>(null);
  const [fuera, setFuera] = useState(false);
  const temporizador = useRef<number | undefined>(undefined);

  const margen = paso?.margenResaltado ?? 10;

  const medir = useCallback(() => {
    if (!elementoObjetivo) {
      setCaja(null);
      return;
    }
    const r = unirConExpandido(
      elementoObjetivo.getBoundingClientRect(),
      paso?.selectorExpandido
    );
    setCaja({
      top: r.top - margen,
      left: r.left - margen,
      width: r.width + margen * 2,
      height: r.height + margen * 2,
    });
  }, [elementoObjetivo, paso?.selectorExpandido, margen]);

  /* ---------- Remedición ---------- */

  useEffect(() => {
    medir();
  }, [medir, paso]);

  useEffect(() => {
    if (!elementoObjetivo) return;
    const alCambiar = () => medir();
    window.addEventListener("resize", alCambiar);
    window.addEventListener("scroll", alCambiar, true);
    return () => {
      window.removeEventListener("resize", alCambiar);
      window.removeEventListener("scroll", alCambiar, true);
    };
  }, [elementoObjetivo, medir]);

  useEffect(() => {
    if (!elementoObjetivo) return;
    const obs = new ResizeObserver(() => medir());
    obs.observe(elementoObjetivo);
    return () => obs.disconnect();
  }, [elementoObjetivo, medir]);

  useEffect(() => {
    if (!elementoObjetivo || !paso?.selectorExpandido) return;
    if (typeof MutationObserver === "undefined") return;
    const obs = new MutationObserver(() => medir());
    obs.observe(document.body, { childList: true, subtree: true });
    return () => obs.disconnect();
  }, [elementoObjetivo, paso?.selectorExpandido, medir]);

  /**
   * El ResizeObserver sólo detecta cambios de tamaño del objetivo. Si el
   * objetivo se desplaza porque un ancestro reacomoda el layout (el sidebar
   * al contraerse, por ejemplo) sin cambiar su propio tamaño, ese observer
   * no se entera. Por eso, al cambiar de paso se remide en cada frame
   * durante un instante corto.
   */
  useEffect(() => {
    if (!elementoObjetivo) return;
    const inicio = performance.now();
    let frame: number;
    const tick = (ahora: number) => {
      medir();
      if (ahora - inicio < 600) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [elementoObjetivo, paso, medir]);

  /* ---------- Velo reactivo ----------
     Se atenúa sólo cuando el usuario interactúa FUERA del área enfocada.
     Dentro del recorte no hay nada que demostrar: aclarar el velo ahí sería
     ruido justo cuando está usando lo que le señalamos. */
  useEffect(() => {
    if (!elementoObjetivo) return;

    const alInteractuar = (e: Event) => {
      const destino = e.target as Node | null;
      if (!destino) return;
      if (elementoObjetivo.contains(destino)) {
        // Volvió al foco: se restaura de inmediato, sin esperar el temporizador.
        window.clearTimeout(temporizador.current);
        setFuera(false);
        return;
      }
      if (refTarjeta.current?.contains(destino)) return; // la propia tarjeta no cuenta

      setFuera(true);
      window.clearTimeout(temporizador.current);
      temporizador.current = window.setTimeout(() => setFuera(false), 2600);
    };

    document.addEventListener("focusin", alInteractuar);
    document.addEventListener("pointerdown", alInteractuar, true);
    return () => {
      document.removeEventListener("focusin", alInteractuar);
      document.removeEventListener("pointerdown", alInteractuar, true);
      window.clearTimeout(temporizador.current);
    };
  }, [elementoObjetivo, refTarjeta]);

  // Al cambiar de paso, el velo siempre vuelve a su estado pleno.
  useEffect(() => {
    window.clearTimeout(temporizador.current);
    setFuera(false);
  }, [paso]);

  /* ---------- Traer la sección a la vista ---------- */
  useEffect(() => {
    if (!elementoObjetivo) return;
    const r = elementoObjetivo.getBoundingClientRect();
    if (r.top < 12 || r.bottom > window.innerHeight - 12) {
      try {
        elementoObjetivo.scrollIntoView({
          block: "center",
          behavior: reduceMotion() ? "auto" : "smooth",
        });
      } catch {
        /* entornos sin scrollIntoView: la posición ya quedó calculada */
      }
    }
  }, [elementoObjetivo, paso]);

  return { caja, fuera, medir };
}
